import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import accountsImage from '../../assets/zz.png';

// System Card Component
const SystemCard = ({ title, onClick, disabled = false }) => {
  return (
    <div
      onClick={!disabled ? onClick : undefined}
      className={`group relative overflow-hidden bg-white rounded-xl border 
        ${disabled 
          ? 'border-gray-200 opacity-50 cursor-not-allowed' 
          : 'border-gray-100 cursor-pointer hover:shadow-xl hover:-translate-y-1'
        } 
        transition-all duration-300`}
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#C5A572]/5 to-transparent rounded-bl-full" />
      <div className="p-8">
        <h3 className={`text-xl font-semibold ${disabled 
          ? 'text-gray-400' 
          : 'text-gray-900 group-hover:text-[#C5A572]'} 
          transition-colors`}
        >
          {title}
        </h3>
        {disabled && (
          <p className="mt-2 text-sm text-gray-400">
            Access restricted
          </p>
        )}
      </div>
      
      {!disabled && (
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-[#C5A572] to-[#DBC4A7] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
      )}
    </div>
  );
};

// Top Navigation Component
const TopNav = ({ onLogout }) => {
  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          <div className="flex items-center space-x-8">
            <img 
              src={phoenicianLogo} 
              alt="Phoenician" 
              className="h-12"
            />
            <div className="h-8 w-px bg-gray-200" />
            <span className="text-lg text-gray-700">Accounts</span>
          </div>
          
          <div className="flex items-center">
            <button
              onClick={onLogout}
              className="inline-flex items-center px-6 py-2.5 border-2 border-[#C5A572] text-sm font-medium rounded-lg text-[#C5A572] hover:bg-[#C5A572] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C5A572] transition-all duration-200"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Welcome Banner Component
const WelcomeBanner = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const displayName = user?.displayName || user?.username?.split('@')[0] || 'User';

  return (
    <div className="relative overflow-hidden bg-[#1a1f2e] rounded-xl mb-8">
      <div className="flex justify-between items-center">
        {/* Text Content */}
        <div className="p-8 text-white">
          <h1 className="text-3xl font-semibold mb-2">
            Phoenician Technical Services LLC
          </h1>
          <p className="text-white/90">Hello {displayName}</p>
        </div>

        {/* Image Container */}
        <div className="h-full w-[450px] relative overflow-hidden">
          <img 
            src={accountsImage} 
            alt="Accounts Team" 
            className="h-full w-full object-cover object-center"
            style={{
              clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)'
            }}
          />
          
          {/* Subtle gradient overlay */}
          <div 
            className="absolute inset-0" 
            style={{
              background: 'linear-gradient(90deg, #1a1f2e 0%, transparent 10%)',
              clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)'
            }}
          />
        </div>
      </div>
    </div>
  );
};


// Systems Grid Component
const SystemsGrid = ({ onSystemSelect }) => {
  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.role?.toLowerCase();
  const canAccessOtherInvoices = !['loams_viewer', 'loams_only'].includes(userRole);

  return (
    <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Phoenician Accounts Team</h2>
        <p className="text-gray-500">Select an accounting system to manage invoices and transactions</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <SystemCard
          title="Other Invoices"
          onClick={() => onSystemSelect('accounts1')}
          disabled={!canAccessOtherInvoices}
        />
        
        <SystemCard
          title="LOAMS Management"
          onClick={() => onSystemSelect('loams')}
        />
        <SystemCard
  title="Petty Cash Expense"
  onClick={() => onSystemSelect('petty-cash')}
  disabled={!canAccessOtherInvoices}
/>
        <SystemCard
          title="MIS REPORTS"
          onClick={() => onSystemSelect('MIS')}
          disabled={!canAccessOtherInvoices}
        />
      </div>
    </div>
  );
};


// Main Dashboard Component
const AccountsDashboard = () => {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleSystemSelect = (path) => {
    navigate(`/${path}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNav onLogout={handleLogout} />
      
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <WelcomeBanner />
        <SystemsGrid onSystemSelect={handleSystemSelect} />
      </main>
    </div>
  );
};

export default AccountsDashboard;
